import { FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';

import { Icon, Button, Region } from '@eon-home/react-library';

import { SpotPriceRegionModel } from '@swagger-http';

import { Scope } from '@tools/enums';
import { Nullable } from '@tools/types';
import { WithScopes } from '@hocs/with-scopes';
import { setBillingRegion } from '@store/actions';
import { WithErrorAndLoader } from '@hocs/with-error-and-loader';
import { getRegion, checkForScopes } from '@tools/utils';
import { useBillingRegion, useBillingRegions } from '@store/selectors';

import PinIcon from '@assets/icons/misc/pin.svg';

import './index.scss';

export interface MapProps {
    regions: Nullable<SpotPriceRegionModel[]>;
    onSelect: (region: SpotPriceRegionModel) => void;
    selectedRegion?: Nullable<SpotPriceRegionModel>;
}

const maps: Partial<Record<Region, FC<MapProps>>> = {};

interface Props {
    force?: boolean;
    onSelect?: () => void;
}

export const BillingRegion: FC<Readonly<Props>> = ({
    force = false,
    onSelect,
}: Props) => {
    const { t } = useTranslation();
    const country = getRegion();
    const dispatch = useDispatch();

    const {
        data: region,
        error: regionError,
        loading: regionLoading,
    } = useBillingRegion();
    const {
        data: regions,
        error: regionsError,
        loading: regionsLoading,
    } = useBillingRegions();

    // prettier-ignore
    const [selectedRegion, setSelectedRegion] = useState<SpotPriceRegionModel | null>(region);

    const Map = maps[country];
    const error = regionError || regionsError;
    const loading = regionLoading || regionsLoading;

    return !Map ? null : (
        <WithErrorAndLoader
            error={error}
            loading={loading}
            className="c-billing-region c-billing-region--empty"
        >
            <div className="c-billing-region">
                <h2>
                    <Icon src={PinIcon} /> {t('Spot price region')}
                </h2>

                <p>
                    {t(
                        'Please select below the region you live in by tapping on the areas we suggested, so we can show you the correct spot prices.',
                    )}
                </p>

                {region === null || force ? (
                    <>
                        <Map
                            regions={regions}
                            onSelect={(region: SpotPriceRegionModel) => {
                                if (
                                    !checkForScopes([
                                        Scope.BILLING_REGION_WRITE,
                                    ])
                                ) {
                                    return;
                                }

                                setSelectedRegion(region);
                            }}
                            selectedRegion={selectedRegion}
                        />

                        <div className="c-billing-region__actions">
                            {!!selectedRegion && (
                                <p>
                                    <Trans
                                        defaults={t(
                                            "You've selected region: <strong>{{ region }}</strong>",
                                            {
                                                region: selectedRegion.displayName,
                                            },
                                        )}
                                        components={{
                                            strong: <strong />,
                                        }}
                                    />
                                </p>
                            )}

                            <Button
                                type="button"
                                small
                                disabled={!selectedRegion}
                                onClick={() => {
                                    if (!selectedRegion) {
                                        return;
                                    }

                                    dispatch(
                                        setBillingRegion(
                                            selectedRegion,
                                            onSelect,
                                        ),
                                    );
                                }}
                            >
                                {t('Save')}
                            </Button>
                        </div>
                    </>
                ) : null}
            </div>
        </WithErrorAndLoader>
    );
};

export default (props: Props) => (
    <WithScopes requiredScopes={[Scope.BILLING_REGION_READ]}>
        <BillingRegion {...props} />
    </WithScopes>
);
